/**
 * Algaplay — Dental Professional Gate
 * Modal i18n consent gate for /collections/dental-materials
 * Detects locale from <html lang="...">. Persists 30d via localStorage.
 * Author: Marco (web dev) — 2026-04-28
 */
(function () {
  "use strict";

  var TRANSLATIONS = {
    en: {
      title: "Professional Access",
      message: "This section is reserved for dental professionals. Do you confirm that you are a dental professional?",
      confirm: "Confirm",
      cancel: "Cancel"
    },
    it: {
      title: "Accesso riservato ai professionisti",
      message: "Questa sezione è riservata ai professionisti del settore odontoiatrico. Conferma di essere un professionista del settore dentale?",
      confirm: "Conferma",
      cancel: "Annulla"
    },
    fr: {
      title: "Accès réservé aux professionnels",
      message: "Cette section est réservée aux professionnels dentaires. Confirmez-vous être un professionnel du secteur dentaire ?",
      confirm: "Confirmer",
      cancel: "Annuler"
    },
    de: {
      title: "Zugang für Fachpersonal",
      message: "Dieser Bereich ist ausschließlich Dentalfachpersonal vorbehalten. Bestätigen Sie, dass Sie zahnmedizinisches Fachpersonal sind?",
      confirm: "Bestätigen",
      cancel: "Abbrechen"
    },
    es: {
      title: "Acceso reservado a profesionales",
      message: "Esta sección está reservada a profesionales del sector dental. ¿Confirma que es un profesional del sector dental?",
      confirm: "Confirmar",
      cancel: "Cancelar"
    },
    ar: {
      title: "دخول مخصّص للمختصّين",
      message: "هذا القسم مخصّص للمختصّين في طب الأسنان. هل تؤكّد أنّك مختصّ في مجال طب الأسنان؟",
      confirm: "تأكيد",
      cancel: "إلغاء"
    }
  };

  var STORAGE_KEY = "algaplay_dental_gate_confirmed_at";
  var TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  var PATH_REGEX = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/dental-materials(?:\/|$|\?)/i;

  function detectLocale() {
    var lang = (document.documentElement.getAttribute("lang") || "en").toLowerCase().split("-")[0];
    return TRANSLATIONS[lang] ? lang : "en";
  }

  function pathMatches() {
    return PATH_REGEX.test(window.location.pathname);
  }

  function alreadyConfirmed() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      var ts = parseInt(raw, 10);
      if (isNaN(ts)) return false;
      return (Date.now() - ts) < TTL_MS;
    } catch (e) { return false; }
  }

  function persistConfirm() {
    try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (e) {}
  }

  function buildModal(locale) {
    var t = TRANSLATIONS[locale];
    var isRTL = (locale === "ar");

    var overlay = document.createElement("div");
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "algaplay-dpg-title");
    overlay.setAttribute("aria-describedby", "algaplay-dpg-message");
    if (isRTL) overlay.setAttribute("dir", "rtl");
    overlay.style.cssText = [
      "position:fixed", "inset:0", "top:0", "left:0",
      "width:100%", "height:100%",
      "background:rgba(0,0,0,0.55)",
      "display:flex", "align-items:center", "justify-content:center",
      "z-index:2147483647",
      "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif"
    ].join(";");

    var box = document.createElement("div");
    // DE message ~115ch — give it room
    box.style.cssText = [
      "background:#fff",
      "padding:28px 32px",
      "border-radius:10px",
      "max-width:560px",
      "width:calc(100% - 40px)",
      "box-shadow:0 10px 40px rgba(0,0,0,0.25)",
      "text-align:" + (isRTL ? "right" : "left")
    ].join(";");

    var title = document.createElement("h2");
    title.id = "algaplay-dpg-title";
    title.textContent = t.title;
    title.style.cssText = "margin:0 0 12px 0;font-size:1.25rem;font-weight:600;color:#111;";

    var msg = document.createElement("p");
    msg.id = "algaplay-dpg-message";
    msg.textContent = t.message;
    msg.style.cssText = "margin:0 0 20px 0;font-size:0.95rem;line-height:1.5;color:#333;";

    var btnWrap = document.createElement("div");
    btnWrap.style.cssText = "display:flex;gap:10px;justify-content:" + (isRTL ? "flex-start" : "flex-end") + ";flex-wrap:wrap;";

    var baseBtn = "padding:10px 20px;border-radius:6px;border:1px solid transparent;font-size:0.95rem;cursor:pointer;font-weight:500;min-width:110px;";

    var cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.textContent = t.cancel;
    cancelBtn.style.cssText = baseBtn + "background:#f2f2f2;color:#333;border-color:#ccc;";
    cancelBtn.addEventListener("click", function () {
      window.location.replace("https://algaplay.com/");
    });

    var confirmBtn = document.createElement("button");
    confirmBtn.type = "button";
    confirmBtn.textContent = t.confirm;
    confirmBtn.style.cssText = baseBtn + "background:#111;color:#fff;";
    confirmBtn.addEventListener("click", function () {
      persistConfirm();
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.body.style.overflow = "";
    });

    // Convention: Cancel left of Confirm in LTR; in RTL the visual flip happens via dir="rtl"
    btnWrap.appendChild(cancelBtn);
    btnWrap.appendChild(confirmBtn);

    box.appendChild(title);
    box.appendChild(msg);
    box.appendChild(btnWrap);
    overlay.appendChild(box);

    return { overlay: overlay, focus: confirmBtn };
  }

  function init() {
    if (!pathMatches()) return;
    if (alreadyConfirmed()) return;

    var locale = detectLocale();
    var built = buildModal(locale);
    document.body.appendChild(built.overlay);
    document.body.style.overflow = "hidden";
    try { built.focus.focus(); } catch (e) {}
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
